import GoogleMapReact, {
  ChangeEventValue,
  ClickEventValue,
  Coords,
  MapOptions,
  Maps,
  Position
} from "google-map-react";

import { Box } from "@mui/material";
import {
  FC,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";

import { useLocale } from "contexts/LocaleContext";
import MapMarker from "components/MapMarker";
import { setMonitoringVehiclesColor } from "utils/String";

const key = process.env.REACT_APP_MAPS_API_KEY ?? "__ENV_VARIABLE_MISSING__";

const defaultMinHeight = 300;
const defaultZoom = 12;
const closeZoom = 15;

const defaultRadius = 20;

const defaultCenter = {
  lat: -25.431040332176853,
  lng: -49.2434949242968
};

export type MapGeolocation = {
  lat: number;
  lng: number;
  weight?: number;

  type?:
    | "documentation"
    | "intelligence"
    | "search_and_seizure"
    | "theft_and_robbery"
    | "automatic_monitoring"
    | "equipment";

  style?: "pin" | "circle" | "car" | "video" | "mobile" | "equipment";

  color?: string;

  onClick?: MouseEventHandler<HTMLDivElement>;
};

const createMapOptions = (maps: Maps): MapOptions => ({
  fullscreenControl: false,

  zoomControlOptions: {
    position: maps.ControlPosition.RIGHT_TOP
  },

  minZoom: 0
});

type Props = {
  minHeight?: number;
  initialCenter?: Coords;
  center?: Coords;
  zoom?: number;
  customControls?: ReactNode;
  heatmapPositions?: Position[];
  onLoad?: (map: google.maps.Map) => void;
  onChange?: (value: ChangeEventValue) => void;
  onClick?: (value: ClickEventValue) => void;
  geolocations?: MapGeolocation[];
};

type HeatmapPosition = Position & {
  weight?: number;
};

const Map: FC<Props> = ({
  minHeight = defaultMinHeight,
  initialCenter,
  center: centerProp,
  zoom: zoomProp,
  onChange,
  onClick,
  customControls,
  children,
  heatmapPositions,
  onLoad,
  geolocations = []
}) => {
  const [internalCenter, setInternalCenter] = useState<Coords | undefined>(
    centerProp ?? initialCenter
  );

  const [internalZoom, setInternalZoom] = useState(
    zoomProp ?? (initialCenter ? closeZoom : defaultZoom)
  );

  // Quem informa center/zoom controla o mapa; sem isso o componente ignora a prop.
  const center = centerProp ?? internalCenter;
  const zoom = zoomProp ?? internalZoom;
  const setCenter = setInternalCenter;
  const setZoom = setInternalZoom;

  const mapRef = useRef<google.maps.Map | null>(null);

  const heatmapOverlayRef = useRef<GoogleMapsOverlay | null>(null);

  const mapKey = JSON.stringify({
    center,
    geolocations
  });

  const { language } = useLocale();

  const [mapLanguage, mapRegion] = language.split("-");

  const fitMapToBounds = useCallback(
    (map: google.maps.Map | null) => {
      if (!map) return;

      const bounds = new google.maps.LatLngBounds();

      const positions = heatmapPositions ? heatmapPositions : geolocations;

      positions.forEach(location => {
        if (
          Number.isFinite(location.lat) &&
          Number.isFinite(location.lng) &&
          location.lat !== 0 &&
          location.lng !== 0
        ) {
          bounds.extend({
            lat: location.lat,
            lng: location.lng
          });
        }
      });

      if (positions.length > 0 && !bounds.isEmpty()) {
        map.fitBounds(bounds);
      }

      if (positions.length <= 1) {
        map.setZoom(defaultZoom);
      }
    },
    [geolocations, heatmapPositions]
  );

  /*
   * Novo heatmap usando deck.gl.
   *
   * Substitui:
   *
   * google.maps.visualization.HeatmapLayer
   *
   * que foi removido da API atual do Google Maps.
   */
  useEffect(() => {
    const map = mapRef.current;

    if (!map) return;

    /*
     * Remove overlay anterior.
     */
    if (heatmapOverlayRef.current) {
      heatmapOverlayRef.current.setMap(null);
      heatmapOverlayRef.current = null;
    }

    if (!heatmapPositions || heatmapPositions.length === 0) {
      return;
    }

    const validPositions = (heatmapPositions as HeatmapPosition[]).filter(
      position => Number.isFinite(position.lat) && Number.isFinite(position.lng)
    );

    if (validPositions.length === 0) {
      return;
    }

    const heatmapLayer = new HeatmapLayer<HeatmapPosition>({
      id: "lince-heatmap",

      data: validPositions,

      /*
       * deck.gl usa:
       *
       * [longitude, latitude]
       *
       * e não:
       *
       * [latitude, longitude]
       */
      getPosition: (position: HeatmapPosition) => [position.lng, position.lat],

      /*
       * Mantém suporte ao peso que já existia
       * no heatmap antigo.
       */
      getWeight: (position: HeatmapPosition) => position.weight ?? 1,

      radiusPixels: defaultRadius,

      aggregation: "SUM",

      /*
       * Evita recalcular agressivamente
       * durante movimentos do mapa.
       */
      debounceTimeout: 200
    });

    const overlay = new GoogleMapsOverlay({
      layers: [heatmapLayer]
    });

    overlay.setMap(map);

    heatmapOverlayRef.current = overlay;

    return () => {
      overlay.setMap(null);

      if (heatmapOverlayRef.current === overlay) {
        heatmapOverlayRef.current = null;
      }
    };
  }, [heatmapPositions]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        position: "relative"
      }}
    >
      <GoogleMapReact
        key={mapKey}
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{
          key,
          region: mapRegion,
          language: mapLanguage,

          /*
           * "visualization" foi removido.
           *
           * Mantenha "places" apenas se o projeto
           * realmente usa Places.
           */
          libraries: ["places"]
        }}
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;

          if (onLoad && map) {
            onLoad(map);
          }

          if (map && !center) {
            fitMapToBounds(map);
          }
        }}
        defaultCenter={defaultCenter}
        center={center}
        defaultZoom={defaultZoom}
        zoom={zoom}
        options={createMapOptions}
        onChange={onChange}
        onClick={onClick}
      >
        {geolocations
          .filter(
            location =>
              Number.isFinite(location.lat) &&
              Number.isFinite(location.lng) &&
              location.lat !== 0 &&
              location.lng !== 0
          )
          .map((location, idx) => {
            /*
             * Limita coordenadas aos valores
             * geográficos permitidos.
             */
            const latitude = Math.max(-90, Math.min(90, location.lat));

            const longitude = Math.max(-180, Math.min(180, location.lng));

            const markerProps: MapGeolocation = {
              lat: latitude,
              lng: longitude
            };

            if (location.weight !== undefined) {
              markerProps.weight = location.weight;
            }

            if (location.style) {
              markerProps.style = location.style;
            }

            if (location.type) {
              markerProps.type = location.type;

              markerProps.color = setMonitoringVehiclesColor(location.type);
            }

            /*
             * Cor explicitamente recebida tem
             * prioridade sobre a cor por type.
             */
            if (location.color) {
              markerProps.color = location.color;
            }

            markerProps.onClick = event => {
              setCenter({
                lat: latitude,
                lng: longitude
              });

              setZoom(closeZoom);

              if (location.onClick) {
                location.onClick(event);
              }
            };

            return (
              <MapMarker
                key={`equipment-${idx}`}
                {...markerProps}
                onClick={markerProps.onClick}
              />
            );
          })}

        {children}
      </GoogleMapReact>

      {customControls}
    </Box>
  );
};

export default Map;
